"use client";

import { useMemo } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { getPanchang } from "@/lib/api";
import { PanchangCityProvider, usePanchangCity } from "@/contexts/PanchangCity";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";

// All 30 tithis (Shukla 1-15, Krishna 1-14, Amavasya)
const TITHI_DATA: Record<
  string,
  {
    hi: string;
    type: "Nanda" | "Bhadra" | "Jaya" | "Rikta" | "Poorna";
    significance: string;
    auspicious: boolean;
  }
> = {
  Pratipada: {
    hi: "प्रतिपदा",
    type: "Nanda",
    significance: "Auspicious for new beginnings, business, and travel. Ruled by Agni (Fire).",
    auspicious: true,
  },
  Dwitiya: {
    hi: "द्वितीया",
    type: "Bhadra",
    significance: "Good for all auspicious work, marriage, and ceremonies. Ruled by Brahma.",
    auspicious: true,
  },
  Tritiya: {
    hi: "तृतीया",
    type: "Jaya",
    significance:
      "Favored for work requiring strength and courage. Ruled by Gauri. Hartalika Teej.",
    auspicious: true,
  },
  Chaturthi: {
    hi: "चतुर्थी",
    type: "Rikta",
    significance:
      "Sacred to Lord Ganesha. Avoid new beginnings; ideal for worship and removing obstacles.",
    auspicious: false,
  },
  Panchami: {
    hi: "पंचमी",
    type: "Nanda",
    significance: "Good for learning, medicine, and art. Ruled by the Nagas (serpents).",
    auspicious: true,
  },
  Shashthi: {
    hi: "षष्ठी",
    type: "Bhadra",
    significance: "Favorable for military work and conflict resolution. Ruled by Karttikeya.",
    auspicious: true,
  },
  Saptami: {
    hi: "सप्तमी",
    type: "Jaya",
    significance: "Ruled by Surya (Sun). Good for travel and official work.",
    auspicious: true,
  },
  Ashtami: {
    hi: "अष्टमी",
    type: "Rikta",
    significance: "Sacred to Shiva and Durga. Powerful for worship; avoid auspicious ceremonies.",
    auspicious: false,
  },
  Navami: {
    hi: "नवमी",
    type: "Nanda",
    significance:
      "Ruled by the Pitrs (ancestors). Good for ancestor rites (Shraddha, Pitru Tarpan).",
    auspicious: false,
  },
  Dashami: {
    hi: "दशमी",
    type: "Bhadra",
    significance: "Favorable for all good work. Ruled by Dharmaraja. Vijaya Dashami falls here.",
    auspicious: true,
  },
  Ekadashi: {
    hi: "एकादशी",
    type: "Jaya",
    significance:
      "Most sacred fasting tithi. Ruled by Vishnu. Ideal for fasting, prayers, and spiritual practice.",
    auspicious: true,
  },
  Dwadashi: {
    hi: "द्वादशी",
    type: "Nanda",
    significance: "Break of the Ekadashi fast. Auspicious for charity and giving. Ruled by Hari.",
    auspicious: true,
  },
  Trayodashi: {
    hi: "त्रयोदशी",
    type: "Bhadra",
    significance: "Sacred to Shiva (Pradosh Vrat). Auspicious for spiritual practices and worship.",
    auspicious: true,
  },
  Chaturdashi: {
    hi: "चतुर्दशी",
    type: "Rikta",
    significance:
      "Ruled by Shiva and Kali. Powerful for Tantric practices; avoid auspicious ceremonies.",
    auspicious: false,
  },
  Purnima: {
    hi: "पूर्णिमा",
    type: "Poorna",
    significance:
      "Full Moon — most auspicious Poorna tithi. Sacred for Satyanarayan Puja, Holi, Guru Purnima.",
    auspicious: true,
  },
  Amavasya: {
    hi: "अमावस्या",
    type: "Rikta",
    significance:
      "New Moon — dedicated to ancestors and Pitru worship. Avoid auspicious beginnings; powerful for ancestral rites.",
    auspicious: false,
  },
};

const TYPE_INFO = {
  Nanda: {
    label: "Nanda Tithi",
    color: "#4CAF50",
    desc: "Joyful — good for celebration, socialising, starting new work",
  },
  Bhadra: {
    label: "Bhadra Tithi",
    color: "#2196F3",
    desc: "Auspicious — favorable for all ceremonies and important decisions",
  },
  Jaya: {
    label: "Jaya Tithi",
    color: "#FF9800",
    desc: "Victorious — ideal for work requiring strength or conflict resolution",
  },
  Rikta: {
    label: "Rikta Tithi",
    color: "#9C27B0",
    desc: "Empty — sacred for specific deity worship; avoid new beginnings",
  },
  Poorna: {
    label: "Poorna Tithi",
    color: "#FFD700",
    desc: "Complete — the most auspicious tithis (Purnima)",
  },
};

type PanchangResult = Awaited<ReturnType<typeof getPanchang>>;
type PanchangTithi = PanchangResult["tithi"];
type TithiInfo = (typeof TITHI_DATA)[string];
type TypeInfo = (typeof TYPE_INFO)[keyof typeof TYPE_INFO];

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function TithiTodayClient() {
  return (
    <PanchangCityProvider>
      <TithiTodayContent />
    </PanchangCityProvider>
  );
}

function TithiTodayContent() {
  const { city } = usePanchangCity();
  const dateStr = useMemo(() => todayStr(), []);

  const {
    data: result,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["panchang", city.slug, dateStr],
    queryFn: () =>
      getPanchang({
        date: dateStr,
        latitude: city.latitude,
        longitude: city.longitude,
        tz_offset: 5.5,
      }),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const tithi = result?.tithi;
  const tithiInfo = tithi ? (TITHI_DATA[tithi.name] ?? null) : null;
  const typeInfo = tithiInfo ? TYPE_INFO[tithiInfo.type] : null;

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
      <TithiHeader tithi={tithi} cityName={city.name} />
      <MainTithiCard
        isLoading={isLoading}
        isError={isError}
        tithi={tithi}
        tithiInfo={tithiInfo}
        typeInfo={typeInfo}
      />
      <SignificanceCard isLoading={isLoading} tithi={tithi} tithiInfo={tithiInfo} />
      <PanchangSnapshot isLoading={isLoading} result={result} cityName={city.name} />
      <TithiTypesReference />

      <CalculatorCrossLinks currentPage="tithi-today" />
    </div>
  );
}

function TithiHeader({ tithi, cityName }: { tithi?: PanchangTithi; cityName: string }) {
  const paksha = tithi?.paksha ?? "";
  const isShukla = paksha.toLowerCase().includes("shukla");
  return (
    <div className="text-center mb-8">
      <div className="text-5xl mb-3">{isShukla ? "🌕" : "🌑"}</div>
      <h1
        className="text-2xl sm:text-3xl font-bold mb-2"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Aaj Ki Tithi
      </h1>
      <p className="text-[14px] text-ish-text-muted">
        Today&apos;s Hindu lunar day — {todayDisplayLabel()} · {cityName}
      </p>
    </div>
  );
}

function todayDisplayLabel() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function MainTithiCard({
  isLoading,
  isError,
  tithi,
  tithiInfo,
  typeInfo,
}: {
  isLoading: boolean;
  isError: boolean;
  tithi?: PanchangTithi;
  tithiInfo: TithiInfo | null;
  typeInfo: TypeInfo | null;
}) {
  return (
    <div className="card-sacred p-6 mb-5 text-center">
      {isLoading && <TithiLoading />}
      {isError && (
        <p className="text-[14px] py-8 text-red-600">
          Failed to load today&apos;s tithi. Please refresh.
        </p>
      )}
      {!isLoading && !isError && tithi && (
        <TithiDetails tithi={tithi} tithiInfo={tithiInfo} typeInfo={typeInfo} />
      )}
    </div>
  );
}

function TithiLoading() {
  return (
    <div className="py-8">
      <div
        className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3"
        style={{ borderColor: "var(--ish-accent)", borderTopColor: "transparent" }}
      />
      <p className="text-[13px] text-ish-text-faint">Loading today&apos;s tithi…</p>
    </div>
  );
}

function TithiDetails({
  tithi,
  tithiInfo,
  typeInfo,
}: {
  tithi: PanchangTithi;
  tithiInfo: TithiInfo | null;
  typeInfo: TypeInfo | null;
}) {
  return (
    <>
      <p className="text-[13px] font-semibold uppercase tracking-wider mb-2 text-ish-accent">
        {tithi.paksha}
      </p>
      <h2
        className="text-3xl sm:text-4xl font-bold mb-1"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        {tithi.name}
      </h2>
      {tithiInfo && <p className="text-xl mb-4 text-ish-text-muted">{tithiInfo.hi}</p>}
      <TithiNumberDots tithi={tithi} />
      {typeInfo && <TithiTypeBadge tithi={tithi} tithiInfo={tithiInfo} typeInfo={typeInfo} />}
      {typeInfo && <p className="text-[13px] text-ish-text-muted">{typeInfo.desc}</p>}
    </>
  );
}

function TithiNumberDots({ tithi }: { tithi: PanchangTithi }) {
  return (
    <div className="flex items-center justify-center gap-3 mb-5">
      {Array.from({ length: 15 }, (_, index) => (
        <TithiDot key={index} active={isCurrentTithiDot(tithi, index)} />
      ))}
    </div>
  );
}

function isCurrentTithiDot(tithi: PanchangTithi, index: number) {
  const dotNumber = index + 1;
  const visibleNumber = tithi.number > 15 ? tithi.number - 15 : tithi.number;
  return (
    dotNumber === visibleNumber || (index === 14 && ["Amavasya", "Purnima"].includes(tithi.name))
  );
}

function TithiDot({ active }: { active: boolean }) {
  return (
    <div
      className="w-2 h-2 rounded-full transition-all"
      style={{
        backgroundColor: active ? "var(--ish-accent)" : "var(--ish-border)",
        transform: active ? "scale(1.5)" : "scale(1)",
      }}
    />
  );
}

function TithiTypeBadge({
  tithi,
  tithiInfo,
  typeInfo,
}: {
  tithi: PanchangTithi;
  tithiInfo: TithiInfo | null;
  typeInfo: TypeInfo;
}) {
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold mb-4"
      style={{
        backgroundColor: `${typeInfo.color}18`,
        color: typeInfo.color,
        border: `1px solid ${typeInfo.color}40`,
      }}
    >
      {tithiBadgeIcon(tithi, tithiInfo)} {typeInfo.label}
    </div>
  );
}

function tithiBadgeIcon(tithi: PanchangTithi, tithiInfo: TithiInfo | null) {
  if (tithi.name === "Purnima") return "🌕";
  if (tithi.name === "Amavasya") return "🌑";
  return tithiInfo?.auspicious ? "✓" : "⚐";
}

function SignificanceCard({
  isLoading,
  tithi,
  tithiInfo,
}: {
  isLoading: boolean;
  tithi?: PanchangTithi;
  tithiInfo: TithiInfo | null;
}) {
  if (isLoading || !tithiInfo) return null;
  return (
    <div className="card-sacred p-5 mb-5">
      <h3 className="text-[15px] font-bold mb-3 text-ish-dark">Significance of {tithi?.name}</h3>
      <p className="text-[14px] leading-relaxed text-ish-text-muted">{tithiInfo.significance}</p>
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--ish-border)" }}>
        <p className="text-[13px] text-ish-text-faint">
          <span className="font-semibold text-ish-dark">Auspicious for new work: </span>
          {tithiInfo.auspicious
            ? "Yes — this tithi is favorable for starting new activities"
            : "No — this tithi is better for worship and spiritual practice"}
        </p>
      </div>
    </div>
  );
}

function PanchangSnapshot({
  isLoading,
  result,
  cityName,
}: {
  isLoading: boolean;
  result?: PanchangResult;
  cityName: string;
}) {
  if (isLoading || !result) return null;
  return (
    <div className="card-sacred p-5 mb-5">
      <h3 className="text-[15px] font-bold mb-4 text-ish-dark">
        Today&apos;s Panchang Snapshot · {cityName}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        {panchangSnapshotItems(result).map(({ label, value }) => (
          <SnapshotItem key={label} label={label} value={value} />
        ))}
      </div>
      <div className="mt-3 text-center">
        <Link href="/panchang/" className="text-[13px] font-semibold text-ish-accent">
          See full Panchang →
        </Link>
      </div>
    </div>
  );
}

function panchangSnapshotItems(result: PanchangResult) {
  return [
    { label: "Tithi", value: `${result.tithi.name} (${result.tithi.paksha})` },
    { label: "Nakshatra", value: result.nakshatra.name },
    { label: "Yoga", value: result.yoga.name },
    { label: "Karana", value: result.karana.name },
    {
      label: "Vara (Day)",
      value: result.vara.name_hi
        ? `${result.vara.name} · ${result.vara.name_hi}`
        : result.vara.name,
    },
    { label: "Sunrise", value: result.sunrise },
  ];
}

function SnapshotItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--ish-surface-warm)" }}>
      <p className="text-[11px] uppercase tracking-wider mb-1 text-ish-text-faint">{label}</p>
      <p className="text-[13px] font-semibold text-ish-dark">{value}</p>
    </div>
  );
}

function TithiTypesReference() {
  return (
    <div className="card-sacred p-5 mb-6">
      <h3 className="text-[15px] font-bold mb-4 text-ish-dark">The 5 Types of Tithi</h3>
      <div className="space-y-3">
        {Object.entries(TYPE_INFO).map(([key, info]) => (
          <TithiTypeReferenceItem key={key} info={info} />
        ))}
      </div>
    </div>
  );
}

function TithiTypeReferenceItem({ info }: { info: TypeInfo }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
        style={{ backgroundColor: info.color }}
      />
      <div>
        <p className="text-[13px] font-semibold text-ish-dark">{info.label}</p>
        <p className="text-[12px] text-ish-text-faint">{info.desc}</p>
      </div>
    </div>
  );
}
